import { forwardRef, memo, type Ref } from "react";
import { Pressable } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import { VideoView, type VideoPlayer } from "expo-video";

/**
 * expo-video merges native (PureComponent) and web (forwardRef) VideoView
 * declarations, collapsing the ref type to `{}`. We re-declare the instance
 * methods that exist on both platforms so consumers can call them safely.
 */
export interface VideoViewRef {
  enterFullscreen(): Promise<void>;
  exitFullscreen(): Promise<void>;
  startPictureInPicture(): Promise<void>;
  stopPictureInPicture(): Promise<void>;
}

type VideoPlayerViewProps = {
  player: VideoPlayer;
  isFullscreen: boolean;
  isLandscape: boolean;
  onTap: () => void;
  onFullscreenEnter: () => void;
  onFullscreenExit: () => void;
  onFirstFrameRender: () => void;
};

const VideoPlayerViewInner = forwardRef<VideoViewRef, VideoPlayerViewProps>(
  ({ player, isFullscreen, isLandscape, onTap, onFullscreenEnter, onFullscreenExit, onFirstFrameRender }, ref) => {
    return (
      <Pressable onPress={onTap} style={styles.pressable}>
        <VideoView
          ref={ref as Ref<never>}
          // @ts-expect-error expo-video merges native + web VideoView declarations, creating an unsatisfiable intersection type for `player`
          player={player}
          // Plain inline style (NOT a Unistyles StyleSheet ref). On web, expo-video
          // builds the <video> style as `{ ...StyleSheet.flatten(props.style), objectFit }`.
          // RN's flatten doesn't resolve a Unistyles className-ref into concrete
          // width/height, so the <video> was left with NO dimensions and fell back
          // to its intrinsic size (e.g. 1024×768), which overflow:hidden then cropped.
          // A literal object flattens correctly, so the video fills the box and
          // `contentFit=contain` letterboxes it for any aspect ratio.
          style={VIDEO_VIEW_STYLE}
          contentFit="contain"
          nativeControls={isFullscreen || isLandscape}
          allowsFullscreen={true}
          allowsVideoFrameAnalysis={false}
          onFullscreenEnter={onFullscreenEnter}
          onFullscreenExit={onFullscreenExit}
          onFirstFrameRender={onFirstFrameRender}
        />
      </Pressable>
    );
  },
);

// Literal style object for the web <video>. See the note on the `style` prop
// above for why this can't be a Unistyles StyleSheet ref.
const VIDEO_VIEW_STYLE = { width: "100%", height: "100%", backgroundColor: "#000" } as const;

export const VideoPlayerView = memo(VideoPlayerViewInner);

const styles = StyleSheet.create((theme) => ({
  pressable: {
    // Absolutely fill the parent video box so the Pressable is EXACTLY the
    // box size (400×boxHeight), independent of any flex align-items/align-self
    // on ancestors. A flow-positioned child was sizing to the <video>'s
    // intrinsic height and overflowing, which overflow:hidden then cropped.
    // Absolute + inset:0 pins all four edges to the box.
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: theme.spacing.md,
    borderCurve: "continuous",
    overflow: "hidden",
    backgroundColor: "#000",
  },
}));
